import PropTypes from 'prop-types';
import React, {Component} from 'react';
import {ButtonIcon} from '@homeaway/react-buttons';
import {CHEVRON_LEFT__16, CHEVRON_RIGHT__16} from '@homeaway/svg-defs';
import classNames from 'classnames';
import './Pager.less';

class Pager extends Component {
    static propTypes = {
        /** The aria-label for the Pager container Nav element, required for assistive technology */
        navAriaLabel: PropTypes.string.isRequired,
        /** The aria-label for the Pager Next Button element, required for assistive technology */
        nextAriaLabel: PropTypes.string.isRequired,
        /** The aria-label for the Pager Prev Button element, required for assistive technology */
        prevAriaLabel: PropTypes.string.isRequired,
        /** Optional descriptive string to display next to pager. Ex: "1 - 7 of 38" */
        pageCount: PropTypes.string,
        /** Flag to align the pager to the right */
        alignRight: PropTypes.bool,
        /** Flag to align the pager to the left */
        alignLeft: PropTypes.bool,
        /** Flag to align the pager in the center */
        alignCenter: PropTypes.bool,
        /** Flag to determine if the pager has more results that can be reached clicking the Next button */
        hasNext: PropTypes.bool.isRequired,
        /** Flag to determine if the pager has more results that can be reached clicking the Prev button */
        hasPrev: PropTypes.bool.isRequired,
        /** Event handler for when a user clicks the Next button */
        onNextClick: PropTypes.func.isRequired,
        /** Event handler for when a user clicks the Prev button */
        onPrevClick: PropTypes.func.isRequired,
        /** Optional href attribute for the Next button. Used for clients with disabled JavaScript */
        nextUrl: PropTypes.string,
        /** Optional href attribute for the Prev button. Used for clients with disabled JavaScript */
        prevUrl: PropTypes.string,
        /** By default, the pager buttons are given data-wdio attributes of "pager-previous" and "pager-next".
        If you have more than one on a page you can add a prefix to those two strings by using this property. */
        wdioPrefix: PropTypes.string
    };

    static defaultProps = {
        navAriaLabel: 'Pagination',
        nextAriaLabel: 'Next page',
        prevAriaLabel: 'Previous page',
        hasNext: false,
        hasPrev: false,
        onNextClick: () => {},
        onPrevClick: () => {}
    };

    constructor(props) {
        super(props);

        this.handleNextClick = this.handleNextClick.bind(this);
        this.handlePrevClick = this.handlePrevClick.bind(this);
    }

    handleNextClick(e) {
        e.preventDefault();
        if (this.props.hasNext) {
            this.props.onNextClick();
        }
    }

    handlePrevClick(e) {
        e.preventDefault();
        if (this.props.hasPrev) {
            this.props.onPrevClick();
        }
    }

    render() {
        const {navAriaLabel, nextAriaLabel, prevAriaLabel, alignLeft, alignRight, alignCenter, pageCount, hasNext, hasPrev, nextUrl, prevUrl, wdioPrefix, ...rest} = this.props;
        const pagerClassName = classNames('Pager', {
            'Pager--right': alignRight
        }, {
            'Pager--left': alignLeft
        }, {
            'Pager--center': alignCenter
        });

        const wdioPrevious = wdioPrefix ? `${wdioPrefix}-pager-previous` : 'pager-previous';
        const wdioNext = wdioPrefix ? `${wdioPrefix}-pager-next` : 'pager-next';

        delete rest.onNextClick;
        delete rest.onPrevClick;

        return (
            <nav aria-label={navAriaLabel} {...rest}>
                <ul className={pagerClassName}>
                    {pageCount &&
                        <li role="presentation" className="Pager__li Pager__li--page"><span>{pageCount}</span></li>
                    }
                    <li className="Pager__li Pager__li--previous">
                        <ButtonIcon
                            label={prevAriaLabel}
                            data-wdio={wdioPrevious}
                            circle
                            disabled={!hasPrev}
                            href={prevUrl}
                            svgMarkup={CHEVRON_LEFT__16}
                            aria-label={prevAriaLabel}
                            onClick={this.handlePrevClick}
                        />
                    </li>
                    <li className="Pager__li Pager__li--next">
                        <ButtonIcon
                            label={nextAriaLabel}
                            data-wdio={wdioNext}
                            circle
                            disabled={!hasNext}
                            href={nextUrl}
                            svgMarkup={CHEVRON_RIGHT__16}
                            aria-label={nextAriaLabel}
                            onClick={this.handleNextClick}
                        />
                    </li>
                </ul>
            </nav>
        );
    }
}

export default Pager;
